import { createFramesClient } from "@connect/data/frames/client";
import type { PostMessageClient } from "@connect/post-message/client";
import type { ConnectMessageMap } from "@connect/post-message/protocol";
import { clearCredentials, getCredentials } from "@connect/credentials-storage";
import { apiTargetHeader } from "../../lib/parse-frame-config";
import { resolveDevApiBaseUrl } from "../../lib/resolve-api-base-url.ts";
import { routeAuthorize } from "../../lib/route-authorize.ts";
import { logCredentialsDiagnostics } from "../../lib/credentials-diagnostics.ts";
import { createExternalMessages } from "./external-messages.ts";
import type { FrameConfig } from "./config.ts";

// Codes meaning the sent customer token is invalid for this session (switched
// user / expired / ownership mismatch). Excludes partner-level (`forbidden`) and
// transient (`too_many_requests`, 5xx) so a still-valid token is never discarded.
const STALE_CREDENTIAL_CODES = new Set(["unauthorized", "conflict", "unprocessable_entity"]);

function isStaleCredentialRejection(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }
  const code = (error as Record<string, unknown>).code;
  return typeof code === "string" && STALE_CREDENTIAL_CODES.has(code);
}

export async function checkConnection(
  config: FrameConfig,
  send: PostMessageClient<ConnectMessageMap>["send"],
) {
  const startedAt = performance.now();
  const out = createExternalMessages(send);
  void logCredentialsDiagnostics("check_connection_start");

  // 1. Call POST /partner/authorize. baseUrl is the build-time API base in deployed
  // envs (empty = same-origin via the ground-control proxy in dev). Headers are
  // built explicitly, not via authMiddleware: authorize() treats a resolvable
  // x-client-token as authoritative and never falls back to x-session-token, so
  // attaching one here would silently discard this call's whole reason for being
  // (a fresh, single-use session token).
  const { customerToken, csrfToken } = await getCredentials();
  const headers: Record<string, string> = { "x-session-token": config.sessionToken };
  if (customerToken) {
    headers.Authorization = `Bearer ${customerToken}`;
  }
  if (csrfToken) {
    headers["x-csrf-token"] = csrfToken;
  }
  if (config.hasConnection && config.apiTarget === "mock") {
    // Dev-only: in mock mode with no real customer token, send a mock bearer token
    // so the MSW handler returns an active connection.
    headers.Authorization = "Bearer mock_connected";
  }
  if (config.forceError) {
    headers["x-mock-scenario"] = config.forceError;
  }

  const client = createFramesClient({
    baseUrl: resolveDevApiBaseUrl(config.devMode),
    headers: apiTargetHeader(config),
  });

  // Only self-heal an authenticated probe: was a stored token actually attached?
  const hadStoredToken = Boolean(customerToken);

  let { data, error, response } = await client.POST("/platform/v1/partner/authorize", {
    headers,
  });

  // A stale token (e.g. the partner switched users) is re-attached on every call,
  // so an authenticated probe conflicts forever. Drop it and retry anonymously ->
  // connection_required, so the customer re-auths in one pass. Skipped under
  // forceError to keep dev mock scenarios deterministic.
  if (hadStoredToken && isStaleCredentialRejection(error) && !config.forceError) {
    await logCredentialsDiagnostics("clear_stale_token");
    await clearCredentials();
    // Drop the explicit mock bearer too so the retry is genuinely anonymous.
    const anonymousHeaders = { ...headers };
    delete anonymousHeaders.Authorization;
    ({ data, error, response } = await client.POST("/platform/v1/partner/authorize", {
      headers: anonymousHeaders,
    }));
  }

  const meta = {
    durationMs: Math.round(performance.now() - startedAt),
    httpStatus: response.status,
  };

  if (error || !data) {
    const body = error && typeof error === "object" ? (error as Record<string, unknown>) : {};
    const reason = typeof body.code === "string" ? body.code : "authorization_failed";
    await out.connectionFailedGeneric("Unable to activate connection", { ...meta, reason });
    return;
  }

  await routeAuthorize(data, out, config.publicKey as string, meta, config.skipKyc);
}
